import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Andromeda Galaxy",
    subtitle: "Messier 31, M31, or NGC 224",
    intro: "The Andromeda Galaxy (Messier 31, M31, or NGC 224) is a massive, barred spiral galaxy that serves as the nearest major galactic neighbor to the Milky Way. It is a prominent feature of the Local Group and is visible to the naked eye.",
    keyFacts: "Key Facts About Andromeda (M31):",
    distance: "Distance",
    distanceDesc: "Approximately 2.5 million light-years away from Earth.",
    size: "Size",
    sizeDesc: "Roughly 200,000 to 220,000 light-years across, making it significantly larger in diameter than the Milky Way.",
    structure: "Structure",
    structureDesc: "Classified as a barred spiral galaxy, similar in structure to the Milky Way, though with a different bar formation.",
    stellarContent: "Stellar Content",
    stellarContentDesc: "Contains an estimated one trillion stars (about 10¹²), far exceeding the estimated 100–400 billion stars in the Milky Way.",
    visibility: "Visibility",
    visibilityDesc: "It is the most distant object visible to the naked eye, appearing as a faint, fuzzy patch in the constellation Andromeda. It is best observed in the Northern Hemisphere during the fall.",
    collision: "Collision Course",
    collisionDesc: "The Andromeda Galaxy is moving toward the Milky Way at roughly 300 kilometers per second, with a collision and merger predicted to occur in about 4 to 5 billion years.",
    history: "History",
    historyDesc: "Documented as early as 964 AD by Persian astronomer Abd al-Rahman al-Sufi. It was confirmed as a separate galaxy outside the Milky Way by Edwin Hubble in the 1920s.",
    satellites: "Satellite Galaxies",
    satellitesDesc: "M31 is surrounded by over 20 smaller satellite galaxies, the most prominent being M32 and M110.",
    center: "Center",
    centerDesc: "Observations indicate a \"double nucleus\" at its center, which may represent a disk of blue stars orbiting a supermassive black hole."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Andromedagalaksen",
    subtitle: "Messier 31, M31, eller NGC 224",
    intro: "Andromedagalaksen (Messier 31, M31 eller NGC 224) er en massiv spiralgalakse med stang som er den nærmeste store galaktiske naboen til Melkeveien. Den er et fremtredende trekk ved den lokale gruppen og er synlig for det blotte øye.",
    keyFacts: "Nøkkelfakta om Andromeda (M31):",
    distance: "Avstand",
    distanceDesc: "Omtrent 2,5 millioner lysår fra Jorden.",
    size: "Størrelse",
    sizeDesc: "Omtrent 200 000 til 220 000 lysår i diameter, noe som gjør den betydelig større i diameter enn Melkeveien.",
    structure: "Struktur",
    structureDesc: "Klassifisert som en spiralgalakse med stang, lik i struktur til Melkeveien, men med en annen stangformasjon.",
    stellarContent: "Stjerneinnhold",
    stellarContentDesc: "Inneholder anslagsvis én billion stjerner (ca. 10¹²), langt over de estimerte 100–400 milliarder stjernene i Melkeveien.",
    visibility: "Synlighet",
    visibilityDesc: "Det er det fjerneste objektet som er synlig for det blotte øye, og vises som en svak, uklar flekk i stjernebildet Andromeda. Den observeres best på den nordlige halvkule om høsten.",
    collision: "Kollisjonskurs",
    collisionDesc: "Andromedagalaksen beveger seg mot Melkeveien med omtrent 300 kilometer per sekund, og en kollisjon og fusjon forventes å skje om ca. 4 til 5 milliarder år.",
    history: "Historie",
    historyDesc: "Dokumentert så tidlig som i 964 e.Kr. av den persiske astronomen Abd al-Rahman al-Sufi. Den ble bekreftet som en egen galakse utenfor Melkeveien av Edwin Hubble på 1920-tallet.",
    satellites: "Satellittgalakser",
    satellitesDesc: "M31 er omgitt av over 20 mindre satellittgalakser, de mest fremtredende er M32 og M110.",
    center: "Senter",
    centerDesc: "Observasjoner indikerer en \"dobbel kjerne\" i sentrum, som kan representere en skive av blå stjerner som kretser rundt et supermassivt sort hull."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Galaktyka Andromedy",
    subtitle: "Messier 31, M31 lub NGC 224",
    intro: "Galaktyka Andromedy (Messier 31, M31 lub NGC 224) to masywna spiralna galaktyka z poprzeczką, która jest najbliższym dużym galaktycznym sąsiadem Drogi Mlecznej. Jest wyróżniającą się cechą Grupy Lokalnej i jest widoczna gołym okiem.",
    keyFacts: "Kluczowe fakty o Andromedzie (M31):",
    distance: "Odległość",
    distanceDesc: "Około 2,5 miliona lat świetlnych od Ziemi.",
    size: "Rozmiar",
    sizeDesc: "Około 200 000 do 220 000 lat świetlnych średnicy, co czyni ją znacznie większą średnicą niż Droga Mleczna.",
    structure: "Struktura",
    structureDesc: "Sklasyfikowana jako spiralna galaktyka z poprzeczką, podobna w strukturze do Drogi Mlecznej, choć z inną formacją poprzeczki.",
    stellarContent: "Zawartość gwiezdna",
    stellarContentDesc: "Zawiera szacunkowo jeden bilion gwiazd (około 10¹²), znacznie przewyższając szacowane 100–400 miliardów gwiazd w Drodze Mlecznej.",
    visibility: "Widoczność",
    visibilityDesc: "Jest to najbardziej odległy obiekt widoczny gołym okiem, pojawiający się jako słaba, rozmyta plama w gwiazdozbiorze Andromedy. Najlepiej obserwować ją na półkuli północnej jesienią.",
    collision: "Kurs kolizyjny",
    collisionDesc: "Galaktyka Andromedy zbliża się do Drogi Mlecznej z prędkością około 300 kilometrów na sekundę, a kolizja i fuzja przewidywane są za około 4 do 5 miliardów lat.",
    history: "Historia",
    historyDesc: "Udokumentowana już w 964 roku n.e. przez perskiego astronoma Abd al-Rahmana al-Sufiego. Została potwierdzona jako osobna galaktyka poza Drogą Mleczną przez Edwina Hubble'a w latach 20. XX wieku.",
    satellites: "Galaktyki satelitarne",
    satellitesDesc: "M31 jest otoczona przez ponad 20 mniejszych galaktyk satelitarnych, z których najbardziej widoczne to M32 i M110.",
    center: "Centrum",
    centerDesc: "Obserwacje wskazują na \"podwójne jądro\" w centrum, które może reprezentować dysk niebieskich gwiazd orbitujących wokół supermasywnej czarnej dziury."
  }
};

export default function AndromedaDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-purple-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="andromeda-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="andromeda-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <p className="text-white/90 text-lg leading-relaxed mb-8">
              {t.intro}
            </p>

            <h2 
              className="text-3xl font-bold mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              {t.keyFacts}
            </h2>

            <div className="space-y-6 text-white/80 leading-relaxed">
              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.distance}
                </h3>
                <p>{t.distanceDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.size}
                </h3>
                <p>{t.sizeDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.structure}
                </h3>
                <p>{t.structureDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.stellarContent}
                </h3>
                <p>{t.stellarContentDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.visibility}
                </h3>
                <p>{t.visibilityDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.collision}
                </h3>
                <p>{t.collisionDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.history}
                </h3>
                <p>{t.historyDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.satellites}
                </h3>
                <p>{t.satellitesDesc}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.center}
                </h3>
                <p>{t.centerDesc}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
